import { useCallback, useRef } from "react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";

import { showCustomActionSheet } from "components/CustomActionSheet";
import { showErrorMessage } from "helpers/toastMessage";

type FileChangeHandler = (event: any) => Promise<void>;

export const isMobileUploadDevice = () => {
  if (Capacitor.getPlatform() !== "web") return true;
  if (typeof window === "undefined") return false;

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase()
  );
};

export const useDocumentSourcePicker = (onChange: FileChangeHandler) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCamera = useCallback(
    async (source: CameraSource) => {
      if (Capacitor.getPlatform() === "web") {
        fileInputRef.current?.click();
        return;
      }

      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source,
        saveToGallery: false
      });

      if (!photo.webPath) throw new Error("No photo was returned");

      const path = photo.webPath.startsWith("file:")
        ? Capacitor.convertFileSrc(photo.webPath)
        : photo.webPath;
      const response = await fetch(path);
      const blob = await response.blob();
      const format = photo.format ?? "jpeg";
      const file = new File([blob], `photo.${format}`, { type: `image/${format}` });

      await onChange({ target: { files: [file], value: "" } });
    },
    [onChange]
  );

  const openPicker = useCallback(async () => {
    if (!isMobileUploadDevice()) {
      fileInputRef.current?.click();
      return;
    }

    try {
      const { index } = await showCustomActionSheet({
        title: "Upload Options",
        message: "Select a source",
        options: [
          { title: "Take Photo", icon: "camera" },
          { title: "Choose from Gallery", icon: "images" },
          { title: "Browse Files", icon: "folder" },
          { title: "Cancel", style: "cancel" }
        ],
        displayMode: "auto"
      });

      if (index === 0) await handleCamera(CameraSource.Camera);
      if (index === 1) await handleCamera(CameraSource.Photos);
      if (index === 2) fileInputRef.current?.click();
    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : "Failed to select a photo");
    }
  }, [handleCamera]);

  return { fileInputRef, openPicker };
};
